import { Request } from 'express';
import { NextPageContext } from 'next';
import { useRouter } from 'next/router';
import { ReactElement, useCallback, useMemo } from 'react';
import { TrackPageViewContainer } from '../../../../common/containers/TrackPageViewContainer';
import { getDehydratedState } from '../../../../common/getDehydrateState';
import { requestContext } from '../../../../common/requestContext';
import { DehydratedProps } from '../../../../common/type';
import { useSelectListAndRestData } from '../../../../common/useSelectListAndRestData.hook';
import { Loading } from '../../../../design-system/atoms/Loading/Loading';
import { Layout } from '../../../../design-system/layout/Layout';
import { LandingList } from '../../../../design-system/pages/Landing/LandingList';
import { getLandingListQuery } from '../../../../domain/landing/util/getLandingListQuery';
import { addError } from '../../../../errors/ErrorSlice';
import { thunkSerializeError } from '../../../../errors/thunkSerializeError.function';
import { landingFactory } from '../../../../factory/landingFactory';
import { LandingType, SeriesCardViewLandingListQuery } from '../../../../graphql/generated/types';
import { CommonMobileHeaderType } from '../../../../header/constants';
import { CACHE_ONLY_CONFIG } from '../../../../reactQuery/constant';
import { createQueryClient } from '../../../../reactQuery/createQueryClient';
import { nextReduxWrapper } from '../../../../state/store';
import { useInfiniteSeriesCardViewLandingListQuery } from '../../../../wrapper/landingWrapper';
interface SeriesCardViewLandingListPageProps extends DehydratedProps {
  title: string;
}
function SeriesCardViewLandingListPage({
  title,
  hasErrorQuery
}: SeriesCardViewLandingListPageProps) {
  const router = useRouter();
  const {
    dataKey = '',
    themeKeywordUid
  } = getLandingListQuery(router.query);
  const seriesCardViewLandingListVariables = useMemo(() => ({
    dataKey,
    themeKeywordUid,
    size: PAGE_SIZE
  }), [dataKey, themeKeywordUid]);
  const {
    data: infiniteData,
    fetchNextPage,
    isLoading,
    isSuccess,
    isFetching
  } = useInfiniteSeriesCardViewLandingListQuery(seriesCardViewLandingListVariables, {
    initialPageParam: {
      ...seriesCardViewLandingListVariables,
      page: 0
    },
    getNextPageParam: lastPage => {
      return lastPage.seriesCardViewLandingList?.isEnd ? undefined : {
        ...seriesCardViewLandingListVariables,
        page: (lastPage.seriesCardViewLandingList?.page || 0) + 1
      };
    },
    ...CACHE_ONLY_CONFIG,
    enabled: !hasErrorQuery
  });
  const data = useSelectListAndRestData<SeriesCardViewLandingListQuery, 'seriesCardViewLandingList'>('seriesCardViewLandingList', infiniteData);
  const onClickThemeKeyword = useCallback((keywordUid: string) => {
    if (keywordUid === themeKeywordUid) {
      router.replace(`/landing/series/card/${dataKey}`);
      return;
    }
    router.replace(`/landing/series/card/${dataKey}/theme/${keywordUid}`);
  }, [dataKey, router, themeKeywordUid]);
  const {
    isMobile,
    isWebView
  } = requestContext.requestInfo.display;
  return <>
      {isLoading && <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex-center-full">
          <Loading />
        </div>}
      {isSuccess && <LandingList themeKeywordList={data?.themeKeywordOptList} landingItemList={data?.list ?? []} selectedThemeKeywordUid={data?.selectedThemeKeywordOpt?.param} onClickThemeKeyword={onClickThemeKeyword} isMobile={isMobile} isWebView={isWebView} landingType={LandingType.SeriesCard} title={title} isLoading={isFetching} topImageUrl={data?.topImage} totalCount={data?.totalCount ?? 0} isEnd={data?.isEnd} requestNextPageData={() => fetchNextPage()} />}
      <TrackPageViewContainer customPropsDataKeyList={[]} trackObj={{
      section: '랜딩리스트',
      page: '랜딩리스트',
      actionType: 'Pageview',
      actionName: '리스트_화면',
      page_meta: {
        id: dataKey,
        type: 'reference',
        name: title,
        category: data?.category
      },
      customProps: {
        user_action: 'pv',
        ...(themeKeywordUid !== undefined ? {
          page_keyword: themeKeywordUid
        } : {}),
        view_type: 'series_card_view'
      }
    }} />
    </>;
}
SeriesCardViewLandingListPage.getInitialProps = nextReduxWrapper.getInitialPageProps(store => async ({
  req,
  query
}: NextPageContext): Promise<SeriesCardViewLandingListPageProps> => {
  let title = '';
  const {
    dataKey,
    themeKeywordUid
  } = getLandingListQuery(query);
  const queryClient = createQueryClient();
  if (dataKey) {
    try {
      const variables = {
        dataKey,
        themeKeywordUid,
        size: PAGE_SIZE
      };
      const data = await queryClient.fetchInfiniteQuery(landingFactory.infiniteSeriesCardViewLandingList(variables, {
        initialPageParam: variables,
        getNextPageParam: () => undefined,
        ...CACHE_ONLY_CONFIG
      }, {
        ssrRequest: req as Request
      }));
      title = data?.pages[0].seriesCardViewLandingList?.title || '';
      return {
        title,
        ...getDehydratedState(queryClient)
      };
    } catch (error) {
      store.dispatch(addError({
        error: thunkSerializeError(error)
      }));
      return {
        title,
        hasErrorQuery: true
      };
    }
  }
  return {
    title
  };
});
const PAGE_SIZE = 25;
SeriesCardViewLandingListPage.getLayout = function getLayout(page: ReactElement) {
  return <Layout title={page.props.title} mobileLayoutConfig={{
    shouldShowHeader: true,
    shouldShowFooter: true,
    headerType: CommonMobileHeaderType.TwoDepth
  }} pcLayoutConfig={{
    shouldShowHeader: true,
    shouldShowFooter: true
  }}>
      {page}
    </Layout>;
};
export default SeriesCardViewLandingListPage;